import React, { useState, useEffect, useRef } from 'react';
import { useMapStore } from '../../store/useMapStore';

interface SearchBoxProps {
  className?: string;
}

interface PlaceResult {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

interface PlaceDetails {
  geometry: {
    location: {
      lat: () => number;
      lng: () => number;
    };
  };
  formatted_address?: string;
  name?: string;
}

const SearchBox: React.FC<SearchBoxProps> = ({ className = '' }) => {
  const [query, setQuery] = useState('');
  const [predictions, setPredictions] = useState<PlaceResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { setCenter, setZoom, setActiveMarker, mapData } = useMapStore();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if a marker already exists at the given coordinates (check both active and saved markers)
  const markerExistsAtLocation = (lat: number, lng: number): boolean => {
    // Check active marker
    if (mapData.activeMarker && 
        Math.abs(mapData.activeMarker.lat - lat) < 0.0001 && 
        Math.abs(mapData.activeMarker.lng - lng) < 0.0001) {
      return true;
    }
    
    // Check saved markers
    return mapData.savedMarkers.some(marker => 
      Math.abs(marker.lat - lat) < 0.0001 && Math.abs(marker.lng - lng) < 0.0001
    );
  };

  // Get place predictions using AutocompleteService (reverted for compatibility)
  const getPlacePredictions = async (input: string) => {
    if (!input.trim()) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    const google = (window as any).google;
    if (!google || !google.maps || !google.maps.places) {
      console.error('Google Places API not available');
      return;
    }

    setIsLoading(true);

    try {
      const autocompleteService = new google.maps.places.AutocompleteService();
      const request = {
        input: input,
        componentRestrictions: { country: 'au' }, // Restrict to Australia
        types: ['geocode', 'establishment'], // Include addresses and businesses
      };
      autocompleteService.getPlacePredictions(request, (results: PlaceResult[], status: string) => {
        setIsLoading(false);
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          setPredictions(results);
          setShowDropdown(true);
        } else {
          setPredictions([]);
          setShowDropdown(false);
        }
      });
    } catch (error) {
      console.error('Error getting place predictions:', error);
      setIsLoading(false);
      setPredictions([]);
      setShowDropdown(false);
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    getPlacePredictions(value);
  };

  // Handle place selection
  const handlePlaceSelect = async (place: PlaceResult) => {
    setQuery(place.description);
    setShowDropdown(false);
    setPredictions([]);

    const google = (window as any).google;
    if (!google || !google.maps || !google.maps.places) {
      console.error('Google Places API not available');
      return;
    }

    try {
      const placesService = new google.maps.places.PlacesService(document.createElement('div'));
      
      const placeDetails = await new Promise<PlaceDetails>((resolve, reject) => {
        placesService.getDetails(
          {
            placeId: place.place_id,
            fields: ['geometry', 'name', 'formatted_address']
          },
          (result: PlaceDetails, status: string) => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
              resolve(result);
            } else {
              reject(new Error(`Place details error: ${status}`));
            }
          }
        );
      });

      if (placeDetails && placeDetails.geometry && placeDetails.geometry.location) {
        const lat = placeDetails.geometry.location.lat();
        const lng = placeDetails.geometry.location.lng();
        
        const location = {
          lat,
          lng,
          address: placeDetails.formatted_address || place.description
        };

        // Center map on selected location
        setCenter(location);
        
        // Set zoom to 17 for detailed view
        setZoom(17);
        
        // Add marker only if one doesn't already exist at this location
        if (!markerExistsAtLocation(lat, lng)) {
          setActiveMarker(location);
          console.log('Added new marker for:', location);
        } else {
          console.log('Marker already exists at this location:', location);
        }
      }
    } catch (error) {
      console.error('Error getting place details:', error);
    }
  };

  return (
    <div ref={searchBoxRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          placeholder="Search for places in Australia..."
          className="w-full px-4 py-2 pl-10 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
        
        {/* Search Icon */}
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>

        {/* Loading Spinner */}
        {isLoading && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
          </div>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && predictions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {predictions.map((prediction, index) => (
            <div
              key={prediction.place_id}
              onClick={() => handlePlaceSelect(prediction)}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
            >
              <div className="font-medium text-gray-900">
                {prediction.structured_formatting.main_text}
              </div>
              <div className="text-sm text-gray-500">
                {prediction.structured_formatting.secondary_text}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No results */}
      {showDropdown && predictions.length === 0 && query.trim() && !isLoading && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
          <div className="px-4 py-2 text-gray-500">
            No places found
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBox; 